import React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import InfoCard from './InfoCard';
import WorkflowStepper from './WorkflowStepper';
import { useWorkflowState, useWorkflowActions } from '../../stores/useWorkflowStore';
import {  WORKFLOW_TYPES, SCAN_PROCESS_STEPS, PATHS } from '../../constants/flowConfig';
import { useShallow } from 'zustand/shallow';
import styles from './PatientDashboard.module.css';
import ModelDebugSelector from '../../components/ModelDebugSelector';

const PatientDashboard = () => {
  const [patientData, isLoadingPatientData, currentWorkflow, currentStep] = useWorkflowState(
    useShallow((state) => [
      state.patientData, 
      state.isLoadingPatientData, 
      state.currentWorkflow,
      state.currentStep
    ])
  );
  const { fetchPatientData, setCurrentWorkflow, setCurrentStep, setStepCompletion } = useWorkflowActions();
  const [isFetching, setIsFetching] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // This effect will run whenever patientData changes
    if (patientData && !isLoadingPatientData) {
      console.log("Patient data successfully fetched:", patientData);
      // Mark current phase as complete
      setStepCompletion('FETCH_DETAILS');
      // Move to scan process
      setCurrentWorkflow(WORKFLOW_TYPES.SCAN_PROCESS);
      setCurrentStep(SCAN_PROCESS_STEPS.PREPARING_SCAN);
    }
  }, [patientData, isLoadingPatientData, setStepCompletion, setCurrentWorkflow, setCurrentStep]);

  const fetchPatientButtonOnClick = async () => {
    try {
      setIsFetching(true);
      // Fetch patient data
      await fetchPatientData();
    } catch (error) {
      console.error("Error fetching patient data:", error);
    } finally {
      setIsFetching(false);
    }
  };

  const getStepperButton = () => {
    if (!patientData) {
      return (
        <button
          onClick={fetchPatientButtonOnClick}
          disabled={isFetching}
          className={styles.startButton}
        >
          {isFetching ? 'Fetching...' : 'Fetch Patient Details'}
        </button>
      );
    }

    // If we have patient data, show button to start scan process
    return (
      <button
        onClick={() => navigate(PATHS[SCAN_PROCESS_STEPS.PREPARING_SCAN])}
        className={styles.startButton}
      >
        Start Scan Process
      </button>
    );
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden">
  <Header />

  <main className="flex-1 flex flex-col overflow-hidden max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
    {patientData && (
      <div
        className="
          grid flex-1                /* takes all leftover space */
          grid-cols-1                /* 1 column on xs */
          sm:grid-cols-[repeat(auto-fit,_minmax(300px,_1fr))] /* auto-fit from sm+ */
          gap-6 mb-8
          min-h-0                    /* allow its children to shrink */
        ">
        <InfoCard title="Patient Details"       data={patientData.patientDetails} />
        <InfoCard title="Key Measurements"      data={patientData.keyMeasurements} />
        <InfoCard title="Global Value Reduction" data={patientData.globalReduction} />
      </div>
    )}

    {/* stepper won't grow or shrink */}
    <WorkflowStepper className="flex-shrink-0" />

    {/* footer actions won't grow or shrink */}
    <div className={styles.footerActions}>{getStepperButton()}</div>
  </main>

  {/* Model Debug Selector - for testing different model formats */}
  <ModelDebugSelector />
</div>

  );
};

export default PatientDashboard;